import {
  type ActiveReasoningLevel,
  ALL_REASONING_LEVELS,
  type ReasoningSupport,
} from "../model-parameters.ts";
import type { InferenceGeography, ModelRole, RetentionMode } from "../models.ts";
import { CANONICAL_MODELS } from "./canonical.generated.ts";
import type { CanonicalModel } from "./canonical.ts";
import {
  compareParts,
  FAMILY_IDS,
  type FamilyId,
  isFamilyId,
  LAB_REASONING,
  MODEL_FAMILIES,
  type ModelFamily,
  parsedVersion,
  TIER_IMPLICATIONS,
} from "./families.ts";
import { OFFERING_OVERRIDES, type OfferingOverride } from "./offering-overrides.ts";
import { GENERATED_OFFERINGS } from "./offerings.generated.ts";

/**
 * Model catalogue, offering layer (ADR 0017 §1, model-picking D3): how ESI offers each canonical
 * model on Bedrock, **assembled** from three sources, so nothing downstream changes shape:
 *
 * - `offerings.generated.ts`, written by `bun run --filter @esi/shared catalogue:sync` from the
 *   pinned models.dev snapshot: every Bedrock route of every snapshot model of a lab in
 *   `MODEL_LABS` with a profile in a served geography (key = the foundation model ID, the stored
 *   key of consents, `models.allowedModels` and usage), its display name, routes, prices per
 *   route and lifecycle. Knowledge comes only from the snapshot: a version exists for ESI once
 *   the reviewed pin bump (Renovate's `bun` group, `catalogue:sync` run on its branch) adds it.
 *   Which of these versions a connection can route is discovered at runtime (D6), never here.
 * - The family rules in `families.ts`: retention, `implicationsVersion`, the version from which
 *   reasoning is adaptive, and relative cost and speed from the tier.
 * - `offering-overrides.ts`: per offering key, only exceptions (`priceDivergence`, a reasoning
 *   style, `retired`).
 *
 * `MODEL_KEYS` order (users see it: the `models.allowedModels` default, audit `from`/`to`) is
 * families in `families.ts` order, then versions newest first. Bump `MODEL_CATALOGUE_VERSION`
 * (`models.ts`) on any catalogue change.
 *
 * Prices are the snapshot's per route (the foundation ID, when the snapshot lists it, and each
 * geography's profile), with its context tiers; a call is priced at the route it used. A
 * deliberate difference is an override: the price and, in `priceDivergence`, a reason and
 * upstream's value of each differing field per route, so the drift check fails when another
 * field differs or upstream moves a declared one.
 */

/** USD per million tokens. Cache writes are the 5-minute TTL rate. */
export interface PriceRates {
  readonly input: number;
  readonly output: number;
  readonly cacheRead?: number;
  readonly cacheWrite?: number;
}

/** One route's price. */
export interface OfferingPrice extends PriceRates {
  /**
   * Rates for a whole request whose input context (uncached, cache read and cache write tokens)
   * reaches `contextSize`, ascending; the highest tier reached applies.
   */
  readonly tiers?: readonly (PriceRates & { readonly contextSize: number })[];
}

/** The model ID a price is for: the foundation ID or a geography's profile. */
export type PriceRoute = "foundation" | InferenceGeography;

export interface BedrockOffering {
  /** The canonical model (`<lab>/<model>`) in `canonical.generated.ts`. */
  readonly canonical: string;
  readonly provider: "amazon-bedrock";
  /** Pinned here, so an upstream rename never changes what users see. */
  readonly displayName: string;
  readonly routes: {
    /** The foundation model ID; equal to the offering's key. */
    readonly foundation: string;
    /** The inference profile per geography; the geographies the offering routes through. */
    readonly profiles: Readonly<Partial<Record<InferenceGeography, string>>>;
  };
  /** What using the model implies (spec `model-policy`); `geographies` come from `routes`. */
  readonly implications: {
    /** The Bedrock retention mode this model requires. */
    readonly retention: RetentionMode;
    /** Bedrock: the model provider never receives requests or outputs. */
    readonly providerReceivesData: boolean;
    /** 1 (cheapest) to 4. */
    readonly relativeCost: number;
    /** 1 (slowest) to 3. */
    readonly relativeSpeed: number;
  };
  /** Bumped whenever `implications` change: a consent is to one version. */
  readonly implicationsVersion: number;
  /**
   * How the model reasons natively (model-picking D15): `adaptive` (an effort level) or
   * `budget` (a thinking budget), with the levels it accepts; null when it never reasons.
   * Curated, since the canonical layer only says whether it reasons: Claude Haiku 4.5 reasons
   * upstream, but only with a fixed budget. Context window and maximum output come from the
   * canonical model (`limit`).
   */
  readonly reasoning: ReasoningSupport | null;
  /** The price of each route: the foundation ID and every profile in `routes.profiles`. */
  readonly price: Readonly<Partial<Record<PriceRoute, OfferingPrice>>>;
  /**
   * Declares why a route's `price` differs from the snapshot's price of the same model ID, and
   * upstream's value of exactly the fields that differ, per route (the drift check compares both).
   */
  readonly priceDivergence?: {
    readonly reason: string;
    readonly upstream: Readonly<Partial<Record<PriceRoute, Partial<OfferingPrice>>>>;
  };
  readonly lifecycle:
    | { readonly status: "active" }
    | { readonly status: "deprecated"; readonly since: string };
  /**
   * Retired by hand (ADR 0017 §1, `offering-overrides.ts`): kept for stored values (consents,
   * settings, usage), never a candidate nor probed.
   */
  readonly retired?: true;
}

/** A generated offering (`offerings.generated.ts`): what the snapshot says, nothing curated. */
export interface GeneratedOffering {
  readonly canonical: string;
  readonly displayName: string;
  readonly routes: BedrockOffering["routes"];
  readonly price: BedrockOffering["price"];
  readonly lifecycle: BedrockOffering["lifecycle"];
}

/** The family of a canonical model (`<lab>/<family>`); throws when the catalogue has none. */
function familyOfCanonical(key: string, canonical: CanonicalModel | undefined): ModelFamily {
  const id = canonical?.family ? `${canonical.lab}/${canonical.family}` : undefined;
  if (id === undefined || !isFamilyId(id)) {
    throw new Error(
      `${key}: no family in families.ts for ${canonical?.id ?? "its canonical model"}`,
    );
  }
  return MODEL_FAMILIES[id];
}

/** The levels Nova 2's `reasoningConfig` takes with an output limit set (D15, Q18b). */
const REASONING_CONFIG_LEVELS: readonly ActiveReasoningLevel[] = ["low", "medium"];

/**
 * The reasoning an offering takes (D15, Q18b): none when the canonical model does not reason or
 * its lab's style (`LAB_REASONING`) is `none` or unknown; Nova 2's `reasoningConfig`; for an
 * `anthropic` lab, adaptive from the family's version, else a thinking budget.
 */
export function reasoningOf(
  canonical: CanonicalModel | undefined,
  family: ModelFamily,
): ReasoningSupport | null {
  if (!canonical?.reasoning) {
    return null;
  }
  const style = LAB_REASONING[family.lab] ?? "none";
  if (style === "none") {
    return null;
  }
  if (style === "reasoning-config") {
    return { style: "reasoning-config", levels: REASONING_CONFIG_LEVELS };
  }
  const from = family.adaptiveReasoningFrom;
  const version = parsedVersion(canonical.id, canonical.family);
  const adaptive = from !== null && version.length > 0 && compareParts(version, from) >= 0;
  return { style: adaptive ? "adaptive" : "budget", levels: ALL_REASONING_LEVELS };
}

/** One offering from its generated entry, its family's rules and its override. */
export function assembleOffering(
  key: string,
  generated: GeneratedOffering,
  canonical: CanonicalModel | undefined,
  override: OfferingOverride = {},
): BedrockOffering {
  const family = familyOfCanonical(key, canonical);
  const { relativeCost, relativeSpeed } = TIER_IMPLICATIONS[family.tier];
  return {
    canonical: generated.canonical,
    provider: "amazon-bedrock",
    displayName: generated.displayName,
    routes: generated.routes,
    implications: {
      retention: family.retention,
      // Bedrock: the model provider never receives requests or outputs.
      providerReceivesData: false,
      relativeCost,
      relativeSpeed,
    },
    implicationsVersion: family.implicationsVersion,
    reasoning:
      override.reasoning !== undefined ? override.reasoning : reasoningOf(canonical, family),
    price: { ...generated.price, ...override.price },
    ...(override.priceDivergence && { priceDivergence: override.priceDivergence }),
    lifecycle: generated.lifecycle,
    ...(override.retired && { retired: true }),
  };
}

/** Generated keys in `MODEL_KEYS` order: families in `families.ts` order, newest first. */
function assemblyOrder(): GeneratedKey[] {
  const keys = Object.keys(GENERATED_OFFERINGS) as GeneratedKey[];
  const familyRank = (key: GeneratedKey) => {
    const canonical = CANONICAL_MODELS[GENERATED_OFFERINGS[key].canonical];
    return FAMILY_IDS.indexOf(familyOfCanonical(key, canonical).id as FamilyId);
  };
  const newestFirst = (a: GeneratedKey, b: GeneratedKey) => {
    const ca = CANONICAL_MODELS[GENERATED_OFFERINGS[a].canonical];
    const cb = CANONICAL_MODELS[GENERATED_OFFERINGS[b].canonical];
    const da = ca?.releaseDate ?? "";
    const db = cb?.releaseDate ?? "";
    if (da !== db) {
      return da < db ? 1 : -1;
    }
    return -compareParts(
      parsedVersion(ca?.id ?? a, ca?.family),
      parsedVersion(cb?.id ?? b, cb?.family),
    );
  };
  return keys.sort((a, b) => familyRank(a) - familyRank(b) || newestFirst(a, b));
}

type GeneratedKey = keyof typeof GENERATED_OFFERINGS;

export const BEDROCK_OFFERINGS = Object.fromEntries(
  assemblyOrder().map((key) => [
    key,
    assembleOffering(
      key,
      GENERATED_OFFERINGS[key],
      CANONICAL_MODELS[GENERATED_OFFERINGS[key].canonical],
      OFFERING_OVERRIDES[key],
    ),
  ]),
) as { readonly [K in GeneratedKey]: BedrockOffering };

export type OfferingKey = GeneratedKey;

/**
 * The hand-written role → offerings table of model-catalogue D1, no longer routed: candidates
 * are derived from the effort's family (`deriveCandidates`, model-picking D4). Kept only as the
 * reference the catalogue golden's review compares derived candidates with (task 2.12).
 */
export const DEFAULT_ROLE_ORDER = {
  eu: {
    chat: ["anthropic.claude-sonnet-5-5", "anthropic.claude-sonnet-5"],
    reasoning: ["anthropic.claude-opus-5-5", "anthropic.claude-sonnet-5-5"],
    fast: ["anthropic.claude-haiku-5-5", "anthropic.claude-sonnet-5-5"],
    // OCR and captions: Haiku first for cost; Sonnet as the failover and the second reader of
    // blind re-checks. Every candidate must accept images (`modelAcceptsImages`).
    vision: ["anthropic.claude-haiku-5-5", "anthropic.claude-sonnet-5-5"],
  },
  global: {
    chat: ["anthropic.claude-sonnet-5-5", "anthropic.claude-sonnet-5"],
    reasoning: ["anthropic.claude-fable-5-1", "anthropic.claude-opus-5-5"],
    fast: ["anthropic.claude-haiku-5-5", "anthropic.claude-sonnet-5-5"],
    vision: ["anthropic.claude-haiku-5-5", "anthropic.claude-sonnet-5-5"],
  },
} as const satisfies Record<InferenceGeography, Record<ModelRole, readonly OfferingKey[]>>;
